import React, { useState } from 'react';
import AIDrillButton from './AIDrillButton';

const GrammarUnit11Content = ({ unitData, userPreferences, onMarkComplete, userProgress }) => {
  const [currentSection, setCurrentSection] = useState('overview');

  // Safety check for unit data
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/30 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">No Content Available</h2>
            <p className="text-gray-300">Content for Unit 11 is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const { content } = unitData;

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">連接詞概覽</h3>
        <p className="text-gray-300 mb-6">{content.overview.description}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.overview.types.map((type, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{type.type}</h4>
              <p className="text-yellow-300 font-mono mb-2">{type.examples}</p>
              <p className="text-gray-300">{type.function}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCoordinatingConjunctions = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">對等連接詞</h3>
        <p className="text-gray-300 mb-6">{content.coordinating_conjunctions.description}</p>
        
        <div className="space-y-4">
          {content.coordinating_conjunctions.conjunctions.map((conj, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{conj.word}</h4>
              <p className="text-yellow-300 mb-2">{conj.meaning}</p>
              <p className="text-gray-300 mb-3">{conj.usage}</p>
              <div className="space-y-1">
                {conj.examples.map((example, idx) => (
                  <div key={idx} className="mb-2">
                    <p className="text-blue-300 text-sm">{example}</p>
                    <AIDrillButton
                      example={{ sentence: example, analysis: conj.word, translation: { tc: conj.meaning, sc: conj.meaning, en: example } }}
                      unitData={unitData}
                      userPreferences={userPreferences}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-6 bg-blue-600/20 p-4 rounded-lg border border-blue-500/30">
          <p className="text-blue-300 font-semibold">記憶口訣：{content.coordinating_conjunctions.memory_tip}</p>
        </div>
      </div>
    </div>
  );

  const renderCorrelativeConjunctions = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">相關連接詞</h3>
        <p className="text-gray-300 mb-6">{content.correlative_conjunctions.description}</p>
        
        <div className="space-y-4">
          {content.correlative_conjunctions.pairs.map((pair, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{pair.pair}</h4>
              <p className="text-yellow-300 mb-2">{pair.meaning}</p>
              <p className="text-gray-300 mb-3">{pair.usage}</p>
              <div className="space-y-1">
                {pair.examples.map((example, idx) => (
                  <div key={idx} className="mb-2">
                    <p className="text-blue-300 text-sm">{example}</p>
                    <AIDrillButton
                      example={{ sentence: example, analysis: pair.pair, translation: { tc: pair.meaning, sc: pair.meaning, en: example } }}
                      unitData={unitData}
                      userPreferences={userPreferences}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSubordinatingConjunctions = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">從屬連接詞</h3>
        <p className="text-gray-300 mb-6">{content.subordinating_conjunctions.description}</p>
        
        <div className="space-y-6">
          {content.subordinating_conjunctions.categories.map((category, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{category.category}</h4>
              <div className="space-y-3">
                {category.conjunctions.map((conj, idx) => (
                  <div key={idx} className="bg-gray-600/30 p-3 rounded">
                    <h5 className="text-md font-semibold mb-2 text-purple-300">{conj.word}</h5>
                    <p className="text-yellow-300 mb-2">{conj.meaning}</p>
                    <div className="space-y-1">
                      {conj.examples.map((example, exampleIdx) => (
                        <div key={exampleIdx} className="mb-2">
                          <p className="text-blue-300 text-sm">{example}</p>
                          <AIDrillButton
                            example={{ sentence: example, analysis: conj.word, translation: { tc: conj.meaning, sc: conj.meaning, en: example } }}
                            unitData={unitData}
                            userPreferences={userPreferences}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderConjunctiveAdverbs = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">連接副詞</h3>
        <p className="text-gray-300 mb-6">{content.conjunctive_adverbs.description}</p>
        
        <div className="space-y-6">
          {content.conjunctive_adverbs.categories.map((category, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{category.category}</h4>
              <div className="space-y-3">
                {category.adverbs.map((adverb, idx) => (
                  <div key={idx} className="bg-gray-600/30 p-3 rounded">
                    <h5 className="text-md font-semibold mb-2 text-purple-300">{adverb.word}</h5>
                    <p className="text-yellow-300 mb-2">{adverb.meaning}</p>
                    <div className="space-y-1">
                      {adverb.examples.map((example, exampleIdx) => (
                        <div key={exampleIdx} className="mb-2">
                          <p className="text-blue-300 text-sm">{example}</p>
                          <AIDrillButton
                            example={{ sentence: example, analysis: adverb.word, translation: { tc: adverb.meaning, sc: adverb.meaning, en: example } }}
                            unitData={unitData}
                            userPreferences={userPreferences}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderExercises = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">練習題</h3>
        
        <div className="space-y-6">
          {content.exercises.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{section.name}</h4>
              
              <div className="space-y-4">
                {section.questions.map((question, questionIndex) => (
                  <div key={questionIndex} className="bg-gray-600/30 p-3 rounded">
                    <p className="text-gray-300 mb-3">{question.question}</p>
                    <div className="space-y-2">
                      {Object.entries(question.options).map(([key, option]) => (
                        <div key={key} className="flex items-center">
                          <span className="text-yellow-300 font-mono mr-3">{key}.</span>
                          <span className="text-gray-300">{option}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="bg-gray-800/30 rounded-xl p-4 mb-6">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCurrentSection('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          概覽
        </button>
        <button
          onClick={() => setCurrentSection('coordinating')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'coordinating'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          對等連接詞
        </button>
        <button
          onClick={() => setCurrentSection('correlative')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'correlative'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          相關連接詞
        </button>
        <button
          onClick={() => setCurrentSection('subordinating')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'subordinating'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          從屬連接詞
        </button>
        <button
          onClick={() => setCurrentSection('conjunctive_adverbs')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'conjunctive_adverbs'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          連接副詞
        </button>
        <button
          onClick={() => setCurrentSection('exercises')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'exercises'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          練習題
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2 text-blue-300">
            {unitData.title[userPreferences?.language || 'traditional']}
          </h1>
          <p className="text-gray-300 mb-4">
            {unitData.subtitle[userPreferences?.language || 'traditional']}
          </p>
          <p className="text-gray-400">
            {unitData.description[userPreferences?.language || 'traditional']}
          </p>
        </div>

        {/* Navigation */}
        {renderNavigation()}

        {/* Content */}
        {currentSection === 'overview' && renderOverview()}
        {currentSection === 'coordinating' && renderCoordinatingConjunctions()}
        {currentSection === 'correlative' && renderCorrelativeConjunctions()}
        {currentSection === 'subordinating' && renderSubordinatingConjunctions()}
        {currentSection === 'conjunctive_adverbs' && renderConjunctiveAdverbs()}
        {currentSection === 'exercises' && renderExercises()}

        {/* Complete Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => onMarkComplete && onMarkComplete(unitData.id)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            標記為完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrammarUnit11Content;
